import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  LANGUAGES,
  resolveLanguage,
  SUPPORTED_LANGUAGES,
} from "./config";
import { configureDialogCloseLabel } from "@lyder-talk/ui";

import { en } from "./locales/en";
import { vi } from "./locales/vi";

/**
 * Apply a language to the document.
 *
 * `dir` is set even though every language today is LTR, so an RTL locale is a
 * config row later rather than a second pass over every layout file.
 */
export function applyDocumentLanguage(language: string): void {
  if (typeof document === "undefined") return;
  const { code, intlLocale, dir } = LANGUAGES[resolveLanguage(language)];
  const root = document.documentElement;
  root.lang = code;
  root.dir = dir;
  // Pin the locale for `Intl` defaults (relative time, collation), so any
  // `toLocale*()` call that forgot an explicit locale still lands correctly.
  root.setAttribute("data-locale", intlLocale);
}

/**
 * Read the stored preference without pulling in the storage helpers from
 * `LanguageProvider` — this module runs before React exists.
 *
 * Three sources, in priority order, because they answer different questions:
 *
 *   1. an explicit choice the reader made (localStorage)
 *   2. what their browser asks for — a Vietnamese reader on a fresh device
 *      should land on Vietnamese without hunting for a switcher
 *   3. English
 *
 * A returning reader who has *chosen* Vietnamese and then switches their OS
 * back to English keeps Vietnamese, because the explicit choice outranks the
 * navigator. Without that ordering, changing your phone's language would
 * silently undo a preference you set on purpose.
 */
export function detectInitialLanguage(): string {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;

  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) return resolveLanguage(stored);
  } catch {
    // Safari in private mode throws on read. Fall through to the navigator.
  }

  const candidates = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ];
  for (const tag of candidates) {
    if (!tag) continue;
    const primary = tag.split("-")[0]?.toLowerCase();
    if (primary && (SUPPORTED_LANGUAGES as readonly string[]).includes(primary)) {
      return primary;
    }
  }

  return DEFAULT_LANGUAGE;
}

/**
 * Vietnamese falls back to English for any key not yet translated.
 *
 * That is deliberate rather than sloppy: shipping a half-translated app with
 * the untranslated half showing raw `nav.items.browse` keys is worse than
 * showing English, and a missing-key warning per render is noise. English is
 * also a *safe* fallback here — the learning content is English, so an
 * English string sitting next to an English exercise never looks broken.
 */
void i18next.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    // Cast, not a type error: `vi` is a DeepPartial by design. See the note on
    // DeepPartial in locales/vi.ts.
    vi: { translation: vi as typeof en },
  },
  lng: detectInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  // Vietnamese has no distinct plural categories beyond n (it uses "n + unit"),
  // but Intl.PluralRules gets this right per-locale for free.
  returnNull: false,
  interpolation: {
    // React already escapes for us. Escaping twice turns the apostrophe in
    // "learner's" into a literal &#39; entity in the DOM.
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
  // Missing keys fall back to English silently; `i18n-coverage` reports what
  // is left so the gap stays visible without polluting the console.
});

/**
 * Keep `<html lang>` in step with the active language.
 *
 * Subscribed here, at module scope, rather than in `LanguageProvider`. The
 * provider is the supported way to switch, but it is not the *only* one:
 * `i18n.changeLanguage()` is a plain call anyone can make, and the Zustand
 * assistant store already reads `i18n.resolvedLanguage` directly. Listening
 * for `languageChanged` means the document attribute follows however the
 * language changed, so a stale `lang` — which makes a screen reader pronounce
 * Vietnamese with an English voice — cannot be reached by accident.
 */
applyDocumentLanguage(i18next.resolvedLanguage ?? i18next.language);
i18next.on("languageChanged", applyDocumentLanguage);

// The shared dialog primitive lives in @lyder-talk/ui, which cannot translate
// its own close button; tell it the current language's label instead.
configureDialogCloseLabel(i18next.t("pages.dialogs.close", "Close"));
i18next.on("languageChanged", (language) => {
  configureDialogCloseLabel(i18next.t("pages.dialogs.close", "Close", { lng: language }));
});

export default i18next;